'use client';

import {
  useState,
  useEffect,
  useContext,
  createContext,
  PropsWithChildren,
  useCallback,
} from 'react';
import { usePathname } from 'next/navigation';
import { UUID } from '@trucksmarter/shared';
import { useOnIdle } from '@lib/IdleTimeProvider/IdleTimeProvider';
import useLocalStorage from '../storage/useLocalStorage';

const AnalyticsSessionIdContext = createContext<UUID | null>(null);

export function useAnalyticsSessionId() {
  const analyticsSessionId = useContext(AnalyticsSessionIdContext);

  if (analyticsSessionId === null) {
    throw new Error(
      'useAnalyticsSessionId has to be used within <AnalyticsSessionIdProvider>',
    );
  }

  return analyticsSessionId;
}

export const ANALYTICS_SESSION_ID_KEY = 'analyticsSessionId';

export default function AnalyticsSessionIdProvider({
  children,
  forceSessionRotatePaths,
}: PropsWithChildren<{
  /** Paths that should trigger a session ID rotation when navigated to. */
  forceSessionRotatePaths?: Array<string>;
}>) {
  const pathname = usePathname();
  const [analyticsSessionIdFallback] = useState<UUID>(() => UUID());
  const [analyticsSessionId, setAnalyticsSessionId] = useLocalStorage(
    ANALYTICS_SESSION_ID_KEY,
    analyticsSessionIdFallback,
    {
      writeFallbackValueIfUsed: true,
      noPrefixedKey: true,
    },
  );

  useOnIdle(
    useCallback(() => setAnalyticsSessionId(UUID()), [setAnalyticsSessionId]),
  );

  useEffect(() => {
    if (pathname != null && forceSessionRotatePaths?.includes(pathname)) {
      setAnalyticsSessionId(UUID());
    }
  }, [pathname, forceSessionRotatePaths, setAnalyticsSessionId]);

  return (
    <AnalyticsSessionIdContext.Provider value={analyticsSessionId}>
      {children}
    </AnalyticsSessionIdContext.Provider>
  );
}
